import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Empty } from '@cloudflare/kumo/components/empty';
import { Table } from '@cloudflare/kumo/components/table';
import { Input } from '@cloudflare/kumo/components/input';
import { Select } from '@cloudflare/kumo/components/select';
import {
  DialogRoot,
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@cloudflare/kumo/components/dialog';
import { ArrowCounterClockwise, DownloadSimple, Plus, Trash } from '@phosphor-icons/react';
import { api } from '@/lib/api';

// 少額スポンサー一覧 (読み取り専用)。全経路 (同時申込 / 単独ゲスト / 出展者の後追い) を
// sponsorships テーブルから引いて表示する。編集・返金 UI は持たない
// (返金は Stripe ダッシュボードで手動運用)。年度は 2026 固定
// (少額スポンサー枠は 2026 から導入のため、過年度には存在しない)。
//
// この画面が少額スポンサーの唯一の正データ。出展者一覧・出展者詳細・支払い管理に出る
// sponsorUnits は申込時の仮値で、後追いの追加支援 (addon) を含まないため実績には使えない。
const YEAR = 2026;

type Kind = 'bundled' | 'standalone' | 'addon';

interface SponsorshipRow {
  id: string;
  kind: Kind;
  status: string;
  displayName: string;
  units: number;
  amount: number;
  email: string;
  exhibitorId: string | null;
  paidAt: string | null;
  createdAt: string;
  applicationNumber: number | null;
  exhibitorName: string | null;
  // 運営が手動で記録した行か (銀行振込など Stripe を通らない入金)。取り消しはこの行のみ可。
  isManual: boolean;
}

interface Summary {
  eventYear: number;
  paidCount: number;
  totalAmount: number;
  totalUnits: number;
  byKind: Record<string, number>;
}

const KIND_LABEL: Record<Kind, string> = {
  bundled: '同時申込',
  standalone: '単独',
  addon: '追加',
};
const KIND_VARIANT: Record<Kind, 'primary' | 'secondary' | 'outline'> = {
  bundled: 'secondary',
  standalone: 'primary',
  addon: 'outline',
};

const STATUS_LABEL: Record<string, string> = {
  paid: '入金済み',
  pending: '未完了',
};

const yen = (n: number) => `${(n ?? 0).toLocaleString('ja-JP')} 円`;

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('ja-JP', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string } } }).response;
    return r?.data?.error ?? r?.data?.message ?? '読み込みに失敗しました';
  }
  return e instanceof Error ? e.message : '読み込みに失敗しました';
}

export default function SponsorshipsPage() {
  const [rows, setRows] = useState<SponsorshipRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 決済途中で離脱した申込 (pending) は既定で隠す。金額が確定しておらず集計にも入らないが、
  // 「入金されていないのに一覧に出ない」取りこぼしを追えるよう、明示的に出せるようにする。
  const [includePending, setIncludePending] = useState(false);
  // 銀行振込など Stripe を通らない入金の手動記録。これが無いと /sponsorships から漏れる。
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    kind: 'standalone' as Kind,
    displayName: '',
    units: '1',
    email: '',
    applicationNumber: '',
    paidOn: '',
    note: '',
  });
  const [deleting, setDeleting] = useState<SponsorshipRow | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: SponsorshipRow[]; summary: Summary }>(
        `/api/admin/sponsorships?year=${YEAR}${includePending ? '&includePending=true' : ''}`
      );
      setRows(res.data.data ?? []);
      setSummary(res.data.summary ?? null);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [includePending]);

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await api.get<Blob>(`/api/admin/sponsorships/export?year=${YEAR}`, {
        responseType: 'blob',
      });
      const blob = res.data;
      const cd = res.headers['content-disposition'] as string | undefined;
      const match = cd?.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] ?? `nt-tokyo-sponsorships-${YEAR}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setExporting(false);
    }
  };

  const submitManual = async () => {
    setSaving(true);
    setError(null);
    try {
      // 出展者は「出展者番号」で指定させる (admin が普段扱う ID)。id への解決はここで行う。
      let exhibitorId: string | null = null;
      if (form.kind !== 'standalone') {
        const num = parseInt(form.applicationNumber, 10);
        if (!Number.isFinite(num)) throw new Error('出展者番号を入力してください');
        const found = await api.get<{ data: Array<{ id: string; applicationNumber: number }> }>(
          `/api/admin/exhibitors?year=${YEAR}&limit=500&includeCancelled=true`
        );
        const hit = (found.data.data ?? []).find((r) => r.applicationNumber === num);
        if (!hit) throw new Error(`出展者番号 #${num} が見つかりません`);
        exhibitorId = hit.id;
      }
      await api.post('/api/admin/sponsorships', {
        kind: form.kind,
        displayName: form.displayName.trim(),
        units: parseInt(form.units, 10) || 1,
        email: form.email.trim(),
        exhibitorId,
        paidOn: form.paidOn || undefined,
        note: form.note || undefined,
      });
      setAddOpen(false);
      setForm({ kind: 'standalone', displayName: '', units: '1', email: '', applicationNumber: '', paidOn: '', note: '' });
      await refresh();
    } catch (e) {
      setError(e instanceof Error && !(e as { response?: unknown }).response ? e.message : extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/api/admin/sponsorships/${deleting.id}`);
      setDeleting(null);
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-kumo-default">少額スポンサー</h1>
          <p className="text-sm text-kumo-muted">
            少額スポンサー (1口 5,000円) のお申し込み一覧です。出展申込と同時 (同時申込) /
            出展しない方の単独申込 / 出展者の後追い追加、すべての経路を集約しています。
            返金は Stripe ダッシュボードで個別対応してください。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex shrink-0 items-center gap-2 whitespace-nowrap text-sm text-kumo-muted">
            <input
              type="checkbox"
              checked={includePending}
              onChange={(e) => setIncludePending(e.currentTarget.checked)}
            />
            未完了も表示
          </label>
          <Button
            variant="ghost"
            icon={ArrowCounterClockwise}
            loading={loading}
            onClick={refresh}
          >
            再読込
          </Button>
          <Button variant="secondary" icon={Plus} onClick={() => setAddOpen(true)}>
            手動で追加
          </Button>
          <Button
            variant="secondary"
            icon={DownloadSimple}
            loading={exporting}
            disabled={loading || (summary?.paidCount ?? 0) === 0}
            onClick={handleExport}
          >
            CSV
          </Button>
        </div>
      </div>

      {error && <Banner variant="error">{error}</Banner>}

      {/* ─── 集計 ─── */}
      <Surface>
        <div className="grid grid-cols-2 gap-px bg-kumo-border md:grid-cols-4">
          <Stat label="合計金額 (確定)" value={yen(summary?.totalAmount ?? 0)} />
          <Stat label="合計口数" value={`${summary?.totalUnits ?? 0} 口`} />
          <Stat label="件数 (確定)" value={`${summary?.paidCount ?? 0} 件`} />
          <Stat
            label="経路別 (同時 / 単独 / 追加)"
            value={`${summary?.byKind?.bundled ?? 0} / ${summary?.byKind?.standalone ?? 0} / ${
              summary?.byKind?.addon ?? 0
            }`}
          />
        </div>
      </Surface>

      {/* ─── 一覧 ─── */}
      <Surface>
        <div className="border-b border-kumo-border px-4 py-3">
          <h2 className="text-base font-semibold text-kumo-default">
            お申し込み一覧 ({rows.length})
          </h2>
          <p className="text-xs text-kumo-muted">
            {includePending
              ? '入金済みと、決済途中で離脱した未完了分をあわせて新しい順に表示しています。上の集計は入金済みのみです。'
              : 'お支払い完了 (確定) のお申し込みを新しい順に表示しています。'}
          </p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader />
          </div>
        ) : rows.length === 0 ? (
          <Empty title="少額スポンサーのお申し込みはまだありません" />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>表示名</Table.Head>
                <Table.Head>経路</Table.Head>
                <Table.Head>状態</Table.Head>
                <Table.Head className="text-right">口数</Table.Head>
                <Table.Head className="text-right">金額</Table.Head>
                <Table.Head>メール</Table.Head>
                <Table.Head>出展者</Table.Head>
                <Table.Head>支払日時</Table.Head>
                <Table.Head />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>
                    <span className="text-sm text-kumo-default">{row.displayName}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant={KIND_VARIANT[row.kind] ?? 'outline'}>
                      {KIND_LABEL[row.kind] ?? row.kind}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant={row.status === 'paid' ? 'primary' : 'outline'}>
                      {STATUS_LABEL[row.status] ?? row.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-right font-mono">{row.units}</Table.Cell>
                  <Table.Cell className="text-right font-mono">{yen(row.amount)}</Table.Cell>
                  <Table.Cell>
                    <span className="text-xs text-kumo-muted">{row.email}</span>
                  </Table.Cell>
                  <Table.Cell>
                    {row.applicationNumber != null && row.exhibitorId ? (
                      <Link to={`/exhibitors/${row.exhibitorId}`} className="flex flex-col">
                        <span className="text-sm text-kumo-default underline">
                          #{row.applicationNumber}
                        </span>
                        <span className="text-xs text-kumo-muted">{row.exhibitorName}</span>
                      </Link>
                    ) : (
                      <span className="text-xs text-kumo-muted">— (出展なし)</span>
                    )}
                  </Table.Cell>
                  <Table.Cell>{formatDateTime(row.paidAt)}</Table.Cell>
                  <Table.Cell>
                    {/* 取り消せるのは手動記録のみ。Stripe 決済の記録は確定データなので消さない
                        (返金は Stripe ダッシュボードで対応)。 */}
                    {row.isManual ? (
                      <Button
                        variant="ghost"
                        icon={Trash}
                        aria-label="手動記録を取り消す"
                        onClick={() => setDeleting(row)}
                      />
                    ) : null}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      {/* ─── 手動追加ダイアログ ─── */}
      <DialogRoot open={addOpen} onOpenChange={(open) => { if (!open) setAddOpen(false); }}>
        <Dialog className="flex flex-col gap-4 p-6">
          <DialogTitle>少額スポンサーを手動で追加</DialogTitle>
          <DialogDescription>
            銀行振込など、Stripe を通らない入金を記録します。入金を確認してから登録してください。
            金額は口数 × 5,000円 で自動計算されます。
          </DialogDescription>

          <label className="flex flex-col gap-1 text-sm">
            経路
            <Select
              value={form.kind}
              onValueChange={(v: string | null) => setForm((f) => ({ ...f, kind: (v ?? 'standalone') as Kind }))}
            >
              <Select.Option value="standalone">単独 (出展しない方)</Select.Option>
              <Select.Option value="bundled">同時申込 (出展料と一緒)</Select.Option>
              <Select.Option value="addon">追加 (出展者の後追い)</Select.Option>
            </Select>
          </label>

          {form.kind !== 'standalone' ? (
            <label className="flex flex-col gap-1 text-sm">
              出展者番号
              <Input
                data-testid="sp-manual-applicationNumber"
                placeholder="例: 127"
                value={form.applicationNumber}
                onChange={(e) => {
                const v = e.currentTarget.value;
                setForm((f) => ({ ...f, applicationNumber: v }));
              }}
              />
            </label>
          ) : null}

          <label className="flex flex-col gap-1 text-sm">
            表示名 (公開されます・30字以内)
            <Input
              data-testid="sp-manual-displayName"
              value={form.displayName}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setForm((f) => ({ ...f, displayName: v }));
              }}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm">
              口数
              <Input
                type="number"
                min={1}
                data-testid="sp-manual-units"
                value={form.units}
                onChange={(e) => {
                const v = e.currentTarget.value;
                setForm((f) => ({ ...f, units: v }));
              }}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              入金日 (任意)
              <Input
                type="date"
                data-testid="sp-manual-paidOn"
                value={form.paidOn}
                onChange={(e) => {
                const v = e.currentTarget.value;
                setForm((f) => ({ ...f, paidOn: v }));
              }}
              />
            </label>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            メールアドレス
            <Input
              type="email"
              data-testid="sp-manual-email"
              value={form.email}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setForm((f) => ({ ...f, email: v }));
              }}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            メモ (任意・操作ログに残ります)
            <Input
              data-testid="sp-manual-note"
              placeholder="例: 8/10 振込確認"
              value={form.note}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setForm((f) => ({ ...f, note: v }));
              }}
            />
          </label>

          <p className="text-xs text-kumo-muted">
            登録すると合計 ¥{((parseInt(form.units, 10) || 0) * 5000).toLocaleString('ja-JP')} として
            入金済み扱いで記録されます。受領メールは自動送信されません。
          </p>

          <div className="flex justify-end gap-2">
            <DialogClose render={(p) => <Button {...p} variant="secondary">キャンセル</Button>} />
            <Button onClick={submitManual} loading={saving} icon={Plus}>
              登録する
            </Button>
          </div>
        </Dialog>
      </DialogRoot>

      {/* ─── 取り消し確認 ─── */}
      <DialogRoot
        role="alertdialog"
        open={deleting !== null}
        onOpenChange={(open) => { if (!open) setDeleting(null); }}
      >
        <Dialog className="flex flex-col gap-4 p-6">
          <DialogTitle>この手動記録を取り消しますか?</DialogTitle>
          <DialogDescription>
            {deleting ? (
              <>
                「{deleting.displayName}」({deleting.units} 口 / {yen(deleting.amount)}) の記録を削除します。
                入金そのものは取り消されません。入力ミスの訂正にのみ使ってください。
              </>
            ) : null}
          </DialogDescription>
          <div className="flex justify-end gap-2">
            <DialogClose render={(p) => <Button {...p} variant="secondary">やめる</Button>} />
            <Button onClick={confirmDelete} loading={saving} icon={Trash}>
              取り消す
            </Button>
          </div>
        </Dialog>
      </DialogRoot>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-kumo-base px-4 py-3">
      <div className="text-xs text-kumo-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold text-kumo-default">{value}</div>
    </div>
  );
}
